import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  PackagePlus,
  Plus,
  Truck,
  Calendar,
  Search,
  X,
  ChevronDown,
  Trash2,
  ShieldOff,
  Ban,
  AlertTriangle,
  Lock,
  Pencil,
  UserPlus,
  Printer,
  ScanLine,
  RefreshCw,
  RotateCcw,
  Check,
  Warehouse as WarehouseIcon,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';
import MultiSplitAssignModal from '../components/MultiSplitAssignModal';

/**
 * Goods Receiving / ASN admin page — Feb 2026.
 *
 * Lets a warehouse manager capture an Advance Shipment Notice BEFORE the
 * truck arrives (vehicle #, expected delivery date, per-line prod/exp
 * dates, Hold Pallet toggle, unique ASN #). On "Receive", each line is
 * landed in the Stage zone via the same helper the Excel bulk stock
 * upload uses — spawning a put-away task per line the picker executes
 * to place stock into a permanent (Pallet ID, Location Code). The
 * production/expiry dates ride along so FEFO allocation keeps working.
 *
 * Existing Excel bulk stock upload flow remains completely untouched
 * (it lives on Admin Products/Inventory) — this page is a parallel new
 * module.
 */
const STATUS_STYLES = {
  draft:     'bg-neutral-200 text-neutral-700',
  expected:  'bg-amber-100 text-amber-800',
  received:  'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const emptyLine = () => ({
  product_id: '',
  sku: '',
  name: '',
  size: '',
  size_type: 'size',
  uom: 'ctn',
  qty: '',
  production_date: '',
  expiry_date: '',
});

const emptyForm = () => ({
  vehicle_number: '',
  expected_delivery_date: '',
  production_date: '',
  expiry_date: '',
  hold_pallet: false,
  notes: '',
  customer_name: '',
  container_no: '',
  warehouse_id: '',
  line_items: [emptyLine()],
});

// Feb 2026 v3 — Tally-sheet endpoint accepts the JWT via `?token=` so
// `window.open(...)` on a fresh tab still resolves (browser can't
// attach an Authorization header on a navigation).
const openTallySheet = (asnId) => {
  const t = localStorage.getItem('mr_admin_token') || '';
  window.open(`${API}/admin/asn/${asnId}/tally-sheet?token=${encodeURIComponent(t)}`, '_blank');
};

// Feb 2026 — Stock Receipt (template-matching) opens in a new tab
// once every put-away task on the ASN has completed. Backend 409s if
// put-away is still in progress, so the frontend only exposes the
// button when the ASN card shows all tasks `completed`.
const openStockReceipt = (asnId) => {
  const t = localStorage.getItem('mr_admin_token') || '';
  window.open(`${API}/admin/asn/${asnId}/stock-receipt?token=${encodeURIComponent(t)}`, '_blank');
};

// ---------------------------------------------------------------------
// ReceiptScanRow — one row in the Receipt Scans table.
// Feb 2026 — Super admin (qayum@) can inline-edit qty/pallet/exp/prod
// or force-delete the scan even after put-away is complete. Regular
// admins see a read-only row.
// ---------------------------------------------------------------------
const ReceiptScanRow = ({ scan, asnId, isSuper, authHeaders, onChanged, staffOptions = [] }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    qty: String(scan.qty ?? ''),
    pallet_no: scan.pallet_no || '',
    expiry_date: scan.expiry_date || '',
    production_date: scan.production_date || '',
    scanned_by: (scan.scanned_by || '').toLowerCase(),
    putaway_location_id: scan.putaway_location_id || '',
  });

  const cancel = () => {
    setDraft({
      qty: String(scan.qty ?? ''),
      pallet_no: scan.pallet_no || '',
      expiry_date: scan.expiry_date || '',
      production_date: scan.production_date || '',
      scanned_by: (scan.scanned_by || '').toLowerCase(),
      putaway_location_id: scan.putaway_location_id || '',
    });
    setEditing(false);
  };

  const save = async () => {
    const qtyNum = parseInt(draft.qty, 10);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      alert('Qty must be a positive integer.');
      return;
    }
    if (!(draft.pallet_no || '').trim()) {
      alert('Pallet # cannot be empty.');
      return;
    }
    if (!(draft.expiry_date || '').trim()) {
      alert('Expiry date cannot be empty.');
      return;
    }
    if (!(draft.scanned_by || '').trim()) {
      alert('Scanned By cannot be empty.');
      return;
    }
    // Feb 2026 — Build a delta payload with ONLY the fields that
    // actually changed. Prevents the backend from running batch /
    // stock reconciliation on a location-only edit (dates would look
    // "changed" when the frontend sent back the same values but with
    // slightly different whitespace) and generally makes the PATCH
    // idempotent.
    const trimmedPallet = draft.pallet_no.trim().toUpperCase();
    const trimmedExp = draft.expiry_date.trim();
    const trimmedProd = (draft.production_date || '').trim();
    const trimmedScanner = draft.scanned_by.trim().toLowerCase();
    const trimmedLoc = (draft.putaway_location_id || '').trim().toUpperCase();
    const payload = {};
    if (qtyNum !== Number(scan.qty || 0)) payload.qty = qtyNum;
    if (trimmedPallet !== (scan.pallet_no || '')) payload.pallet_no = trimmedPallet;
    if (trimmedExp !== (scan.expiry_date || '')) payload.expiry_date = trimmedExp;
    if (trimmedProd !== (scan.production_date || '')) payload.production_date = trimmedProd;
    if (trimmedScanner !== (scan.scanned_by || '').toLowerCase()) payload.scanned_by = trimmedScanner;
    if (trimmedLoc !== (scan.putaway_location_id || '')) payload.putaway_location_id = trimmedLoc;
    if (Object.keys(payload).length === 0) {
      // Nothing to save — treat as a no-op and just close edit mode.
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/asn/${asnId}/scan-item/${scan.id}`,
        payload,
        { headers: authHeaders },
      );
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(
      'Permanently delete this scan?\n\nStock balances will be adjusted and the linked put-away task removed. This CANNOT be undone.'
    )) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/admin/asn/${asnId}/scan-item/${scan.id}/force`,
        { headers: authHeaders },
      );
      await onChanged?.();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const s = scan;

  if (editing) {
    return (
      <tr data-testid={`asn-receipt-scan-${s.id}`} className="bg-sky-50/60">
        <td className="py-1.5 pr-3 pl-3 font-mono text-[#0078D4]">{s.item_number}</td>
        <td className="py-1.5 pr-3 text-[#0F172A] max-w-[220px] truncate">{s.product_name || '—'}</td>
        <td className="py-1.5 pr-3 text-right">
          <input
            type="number"
            min={1}
            value={draft.qty}
            onChange={(e) => setDraft((d) => ({ ...d, qty: e.target.value }))}
            className="w-16 h-7 rounded border border-sky-300 px-1.5 text-right tabular-nums text-xs"
            data-testid={`asn-scan-edit-qty-${s.id}`}
          />
        </td>
        <td className="py-1.5 pr-3">
          <input
            type="text"
            value={draft.pallet_no}
            onChange={(e) => setDraft((d) => ({ ...d, pallet_no: e.target.value }))}
            className="w-24 h-7 rounded border border-sky-300 px-1.5 font-mono uppercase text-xs"
            data-testid={`asn-scan-edit-pallet-${s.id}`}
          />
        </td>
        <td className="py-1.5 pr-3">
          <input
            type="date"
            value={draft.expiry_date}
            onChange={(e) => setDraft((d) => ({ ...d, expiry_date: e.target.value }))}
            className="w-32 h-7 rounded border border-sky-300 px-1.5 text-xs"
            data-testid={`asn-scan-edit-exp-${s.id}`}
          />
        </td>
        <td className="py-1.5 pr-3">
          <input
            type="date"
            value={draft.production_date}
            onChange={(e) => setDraft((d) => ({ ...d, production_date: e.target.value }))}
            className="w-32 h-7 rounded border border-sky-300 px-1.5 text-xs"
            data-testid={`asn-scan-edit-prod-${s.id}`}
          />
        </td>
        <td className="py-1.5 pr-3">
          <select
            value={draft.scanned_by}
            onChange={(e) => setDraft((d) => ({ ...d, scanned_by: e.target.value }))}
            className="w-40 h-7 rounded border border-sky-300 px-1.5 text-[11px] bg-white"
            data-testid={`asn-scan-edit-scanned-by-${s.id}`}
          >
            {(() => {
              // Feb 2026 — include the current scanner even if they
              // aren't in the fetched list (e.g. deactivated user),
              // so the dropdown always shows the current value.
              const emails = new Set(staffOptions.map((u) => (u.email || '').toLowerCase()));
              const options = [...staffOptions];
              if (draft.scanned_by && !emails.has(draft.scanned_by)) {
                options.unshift({ email: draft.scanned_by, name: draft.scanned_by });
              }
              return options.map((u) => {
                const em = (u.email || '').toLowerCase();
                return (
                  <option key={em} value={em}>
                    {u.name ? `${u.name} · ${em}` : em}
                  </option>
                );
              });
            })()}
          </select>
        </td>
        <td className="py-1.5 pr-3">
          <input
            type="text"
            value={draft.putaway_location_id}
            onChange={(e) => setDraft((d) => ({ ...d, putaway_location_id: e.target.value }))}
            placeholder="BAY-A12 or blank"
            className="w-28 h-7 rounded border border-sky-300 px-1.5 font-mono uppercase text-xs"
            data-testid={`asn-scan-edit-location-${s.id}`}
            title="Leave blank to send back to Stage"
          />
        </td>
        <td className="py-1.5 pr-3 pl-3 text-right">
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-0.5 rounded-full border border-emerald-500 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 disabled:opacity-60"
              data-testid={`asn-scan-save-${s.id}`}
            >
              <Check className="w-3 h-3" /> Save
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              className="inline-flex items-center gap-0.5 rounded-full border border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-100 text-[10px] font-bold px-2 py-1 disabled:opacity-60"
              data-testid={`asn-scan-cancel-${s.id}`}
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr data-testid={`asn-receipt-scan-${s.id}`}>
      <td className="py-1.5 pr-3 pl-3 font-mono text-[#0078D4]">
        {s.item_number}
        {s.is_extra && (
          <span className="ml-1 px-1 py-0.5 rounded text-[8px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200">extra</span>
        )}
      </td>
      <td className="py-1.5 pr-3 text-[#0F172A] max-w-[220px] truncate">{s.product_name || '—'}</td>
      <td className="py-1.5 pr-3 text-right tabular-nums font-bold">{s.qty} <span className="text-neutral-500 font-normal">{s.uom}</span></td>
      <td className="py-1.5 pr-3 font-mono text-neutral-800">{s.pallet_no}</td>
      <td className="py-1.5 pr-3 text-neutral-600">{s.expiry_date || '—'}</td>
      <td className="py-1.5 pr-3 text-neutral-600">{s.production_date || '—'}</td>
      <td
        className="py-1.5 pr-3 text-neutral-700 text-[10px]"
        title={s.scanned_by || ''}
        data-testid={`asn-scan-scanned-by-${s.id}`}
      >
        {s.scanned_by_name || s.scanned_by || '—'}
      </td>
      <td
        className="py-1.5 pr-3 text-[10px]"
        data-testid={`asn-scan-location-${s.id}`}
      >
        {s.putaway_location_id ? (
          <span className="font-mono font-semibold text-[#0F172A]">{s.putaway_location_id}</span>
        ) : (
          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
            In Stage
          </span>
        )}
      </td>
      {isSuper && (
        <td className="py-1.5 pr-3 pl-3 text-right">
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={busy}
              title="Edit qty / pallet / dates"
              className="inline-flex items-center rounded-full border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 hover:border-[#0078D4] hover:text-[#0078D4] text-[10px] p-1 disabled:opacity-50"
              data-testid={`asn-scan-edit-btn-${s.id}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              title="Permanently delete + reverse stock"
              className="inline-flex items-center rounded-full border border-red-300 bg-white text-red-600 hover:bg-red-50 hover:border-red-500 text-[10px] p-1 disabled:opacity-50"
              data-testid={`asn-scan-delete-btn-${s.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </td>


      )}
    </tr>
  );
};

/**
 * Feb 2026 — compact activity-by chip row rendered at the top of
 * the ASN detail header. Each chip pairs a muted action LABEL with
 * one or more SHORT first names (comma-separated). Renders nothing
 * when no action has been recorded yet.
 */
const AsnActivityByChips = ({ activity, asnNumber }) => {
  if (!activity) return null;
  const rows = [
    { label: 'Created by', value: activity.created ? [activity.created] : [], key: 'created' },
    { label: 'Received by', value: activity.received ? [activity.received] : [], key: 'received' },
    { label: 'Released by', value: activity.released ? [activity.released] : [], key: 'released' },
    { label: 'Scanned by', value: activity.scanned || [], key: 'scanned' },
    { label: 'Put-Away by', value: activity.putaway || [], key: 'putaway' },
  ].filter((r) => Array.isArray(r.value) && r.value.length > 0);
  if (rows.length === 0) return null;
  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-600 bg-white border border-neutral-200 rounded-md px-3 py-1.5"
      data-testid={`asn-activity-by-${asnNumber}`}
    >
      {rows.map((r) => (
        <span
          key={r.key}
          className="inline-flex items-baseline gap-1"
          data-testid={`asn-activity-${r.key}-${asnNumber}`}
        >
          <span className="text-neutral-400">{r.label}</span>
          <span className="font-semibold text-neutral-800">
            {r.value.join(', ')}
          </span>
        </span>
      ))}
    </div>
  );
};


const AdminGoodsReceiving = () => {
  const { authHeaders } = useAdminAuth();
  const { can, isSuper, me } = useAdminPermissions();
  const { toast } = useToast();
  // Feb 2026 — Caller email for the "original picker can edit after
  // receive" permission check (super admin bypasses). Resolved from
  // localStorage (same source as AdminAuthContext).
  const currentEmail = (typeof window !== 'undefined' && localStorage.getItem('mr_admin_email') || '').trim().toLowerCase();

  // Action gates — super admin always passes via `can()`. Sub-admins
  // need the matching `asn.<action>` slug turned on in Admin Users.
  const canCreate       = can('asn.create');
  const canEdit         = can('asn.edit');
  const canEditExpQty   = can('asn.edit_expected_qty');
  const canDelete       = can('asn.delete');
  // Feb 2026 — Audit visibility. Per super-admin directive, the Line
  // Edit History block is visible to:
  //   • Super admins (always, via `can('*')`).
  //   • Any admin-panel user (role === 'admin') — picks up every
  //     admin + sub-admin that logs into the admin UI, which also
  //     covers the original picker (pickers log in with admin creds).
  //   • Legacy sub-admins carrying the explicit `asn.view_edit_history`
  //     slug (kept so previously-granted permissions keep working).
  // Backend never filters the audit payload, so this is purely UI
  // gating; the data is included in every ASN response.
  const canViewEditHistory = isSuper || me?.role === 'admin' || can('asn.view_edit_history');
  // Oct 2026 — Inline Expected-Qty edit on an ASN line. Keyed by
  // `${asn.id}:${lineIndex}` so only ONE row is editable at a time
  // across the whole list.
  const [qtyEditKey, setQtyEditKey] = useState(null);
  const [qtyDraft, setQtyDraft] = useState('');
  const [qtySaving, setQtySaving] = useState(false);
  // Feb 2026 — Inline edit for the top-level `expected_qty` field on
  // the ASN header. Keyed by `asn.id` so only one ASN's header field
  // is editable at a time. Only shown when the user has the
  // `asn.edit_expected_qty` slug AND the ASN is in Expected status
  // OR on Hold. Everyone else sees a read-only tile.
  const [headerQtyEditId, setHeaderQtyEditId] = useState(null);
  const [headerQtyDraft, setHeaderQtyDraft] = useState('');
  const [headerQtySaving, setHeaderQtySaving] = useState(false);
  // Feb 2026 — Post-receive expected-qty edit modal. Opens when a
  // user with permission clicks the pencil icon on a RECEIVED ASN
  // line. Captures a mandatory reason and routes through the audited
  // `/line/{idx}/qty-post-receive` endpoint. Stock is never touched.
  const [postReceiveEdit, setPostReceiveEdit] = useState(null); // { asn, line, lineIndex }
  const [postReceiveQty, setPostReceiveQty] = useState('');
  const [postReceiveReason, setPostReceiveReason] = useState('');
  const [postReceiveSaving, setPostReceiveSaving] = useState(false);
  const canReceive      = can('asn.receive');
  const canCancel       = can('asn.cancel');
  const canHoldRelease  = can('asn.hold_release');
  const canAssign       = can('asn.assign_putaway') || can('warehouse.reassign_picker_inline');

  const [asns, setAsns] = useState([]);
  // Feb 2026 — Per-ASN put-away flag ("fully" | "partial") keyed by
  // asn_number. Drives the Put-away status chip on each row.
  const [putawayFlags, setPutawayFlags] = useState({});

  // Feb 2026 — Compute Fully/Partial Received per ASN from the record
  // itself: scanned_qty per product (sum of `receipt_scans[].qty`) vs
  // declared `line_items[].qty`. Returns 'fully' | 'partial' | ''.
  const computeReceivedFlag = (a) => {
    const scans = Array.isArray(a?.receipt_scans) ? a.receipt_scans : [];
    const lines = Array.isArray(a?.line_items) ? a.line_items : [];
    if (lines.length === 0) return '';
    const scannedByPid = new Map();
    for (const s of scans) {
      const pid = String(s?.product_id || '');
      const q = Number(s?.qty || 0);
      if (!pid || q <= 0) continue;
      scannedByPid.set(pid, (scannedByPid.get(pid) || 0) + q);
    }
    // Also aggregate expected per product_id (lines may be split).
    const expectedByPid = new Map();
    for (const ln of lines) {
      const pid = String(ln?.product_id || '');
      const q = Number(ln?.qty || 0);
      if (!pid || q <= 0) continue;
      expectedByPid.set(pid, (expectedByPid.get(pid) || 0) + q);
    }
    let anyScanned = false;
    let allMet = true;
    for (const [pid, exp] of expectedByPid.entries()) {
      const got = scannedByPid.get(pid) || 0;
      if (got > 0) anyScanned = true;
      if (got < exp) allMet = false;
    }
    if (allMet) return 'fully';
    if (anyScanned) return 'partial';
    return '';
  };
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  // Feb 2026 — `editingAsnId` = null means "new ASN"; a string id
  // means "edit this ASN" and the modal PUTs instead of POSTs on save.
  const [editingAsnId, setEditingAsnId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // 2026-10 bugfix — Complete Receipt is a floor-mode-only action by
  // design, but a fully-scanned ASN with nobody re-opening Floor Mode
  // to click it was getting stuck in `expected` forever (ticket:
  // ASN stuck despite every pallet scanned + put away). Admin can now
  // finish the same already-existing endpoint directly. Tracks the id
  // of the ASN currently mid-request so only that row's button spins.
  const [completingReceiptId, setCompletingReceiptId] = useState(null);
  // Per-line product-search text so the "add item" autocomplete stays
  // sticky per row even while typing.
  const [lineSearch, setLineSearch] = useState({});
  // Feb 2026 — Put-away tasks for the currently-expanded received ASN,
  // plus the list of assignable users. Fetched lazily when the card
  // expands so a 50-ASN list doesn't trigger 50 GETs upfront.
  const [asnTasks, setAsnTasks] = useState({});           // { [asnId]: task[] }
  const [assignableUsers, setAssignableUsers] = useState([]);
  // Feb 2026 — Multi-picker split modal state for the ASN put-away.
  // When set, renders the shared modal scoped to this ASN.
  const [splitAsnId, setSplitAsnId] = useState(null);
  // Feb 2026 — Warehouse choice is mandatory on the New ASN form.
  // Loaded lazily; scoped to the caller's assigned warehouses via
  // /api/admin/warehouses.
  const [warehouses, setWarehouses] = useState([]);
  // Feb 2026 — Paginated ASN list. We page the server-side query so a
  // ~700-row ASN table doesn't block the first render; the UI still
  // feels like one big list because "Load more" simply appends.
  const PAGE_SIZE = 100;
  const [asnTotal, setAsnTotal] = useState(0);
  const [asnOffset, setAsnOffset] = useState(0);
  const [asnLoadingMore, setAsnLoadingMore] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: asnResp }, prodResp, usersResp, whResp, pfResp] = await Promise.all([
        axios.get(`${API}/admin/asn`, {
          headers: authHeaders,
          params: { limit: PAGE_SIZE, offset: 0 },
        }),
        axios
          .get(`${API}/admin/products`, { headers: authHeaders })
          .catch(() => axios.get(`${API}/products`)),
        axios
          .get(`${API}/admin/asn-assignable-users`, { headers: authHeaders })
          .catch(() => ({ data: [] })),
        axios
          .get(`${API}/admin/warehouses`, { headers: authHeaders })
          .catch(() => ({ data: { warehouses: [] } })),
        axios
          .get(`${API}/admin/dashboard/asn-putaway-flags`, { headers: authHeaders })
          .catch(() => ({ data: { flags: {} } })),
      ]);
      // Backwards-compatible: the endpoint returns an array when no
      // `limit` is sent, and `{items,total,...}` when it is.
      const items = Array.isArray(asnResp) ? asnResp : (asnResp?.items || []);
      const total = Array.isArray(asnResp) ? items.length : (asnResp?.total || items.length);
      setAsns(items);
      setAsnTotal(total);
      setAsnOffset(items.length);
      setProducts(Array.isArray(prodResp?.data) ? prodResp.data : []);
      setAssignableUsers(Array.isArray(usersResp?.data) ? usersResp.data : []);
      setWarehouses(Array.isArray(whResp?.data?.warehouses) ? whResp.data.warehouses : []);
      setPutawayFlags((pfResp?.data?.flags) || {});
    } catch (err) {
      toast({ title: 'Load failed', description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productsById = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return asns.filter((a) => {
      // Feb 2026 — Always keep the currently-expanded ASN visible
      // even if its status has since flipped (e.g. `expected` →
      // `received` mid-scan) and no longer matches the status tab.
      // Prevents the card from vanishing under the user's cursor.
      if (a.id === expandedId) return true;
      if (statusFilter === 'hold') {
        if (!a.hold_pallet) return false;
      } else if (statusFilter !== 'all' && a.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      if ((a.asn_number || '').toLowerCase().includes(q)) return true;
      if ((a.vehicle_number || '').toLowerCase().includes(q)) return true;
      for (const ln of (a.line_items || [])) {
        if ((ln.sku || '').toLowerCase().includes(q)) return true;
        if ((ln.name || '').toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [asns, query, statusFilter, expandedId]);

  const openCreate = () => {
    setEditingAsnId(null);
    setForm(emptyForm());
    setLineSearch({});
    setModalOpen(true);
  };

  const openEdit = (a) => {
    // Preload the form with the ASN's current values. Line items get
    // remapped to the modal's shape (qty as string so the number input
    // treats "" as empty). Products list is already loaded so the
    // "Change" button re-triggers the autocomplete on any line.
    setEditingAsnId(a.id);
    setForm({
      vehicle_number: a.vehicle_number || '',
      expected_delivery_date: a.expected_delivery_date || '',
      production_date: a.production_date || '',
      expiry_date: a.expiry_date || '',
      hold_pallet: !!a.hold_pallet,
      notes: a.notes || '',
      customer_name: a.customer_name || '',
      container_no: a.container_no || '',
      warehouse_id: a.warehouse_id || '',
      line_items: (a.line_items || []).map((ln) => ({
        product_id: ln.product_id || '',
        sku: ln.sku || '',
        name: ln.name || '',
        size: ln.size || '',
        size_type: ln.size_type || 'size',
        uom: ln.uom || 'ctn',
        qty: String(ln.qty || ''),
        production_date: ln.production_date || '',
        expiry_date: ln.expiry_date || '',
      })),
    });
    setLineSearch({});
    setModalOpen(true);
  };

  const addLine = () => {
    setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  };

  const removeLine = (idx) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.length === 1 ? [emptyLine()] : f.line_items.filter((_, i) => i !== idx),
    }));
  };

  const patchLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
    }));
  };

  const pickProductForLine = (idx, product) => {
    patchLine(idx, {
      product_id: product.id,
      sku: product.sku || '',
      name: product.name || '',
      size: product.size || '',
      size_type: product.size_type || 'size',
      uom: product.uom || 'ctn',
    });
    setLineSearch((prev) => ({ ...prev, [idx]: '' }));
  };

  const productSuggestionsFor = (idx) => {
    const q = (lineSearch[idx] || '').trim().toLowerCase();
    if (!q) return [];
    return products
      .filter((p) => {
        return (
          String(p.name || '').toLowerCase().includes(q) ||
          String(p.sku || '').toLowerCase().includes(q)
        );
      })
      .slice(0, 8);
  };

  const submit = async () => {
    // Validate: at least one line with product_id + qty > 0.
    const cleanLines = (form.line_items || [])
      .filter((ln) => ln.product_id && Number(ln.qty) > 0)
      .map((ln) => ({
        product_id: ln.product_id,
        sku: ln.sku || '',
        name: ln.name || '',
        size: ln.size || '',
        size_type: ln.size_type || 'size',
        uom: ln.uom || 'ctn',
        qty: Number(ln.qty),
        production_date: ln.production_date || '',
        expiry_date: ln.expiry_date || '',
      }));
    if (cleanLines.length === 0) {
      toast({ title: 'Add at least one item', description: 'Pick a product and enter a qty.' });
      return;
    }
    // Feb 2026 — Warehouse is now mandatory. The receiving path parks
    // every scan on the selected warehouse's STAGE zone, so a missing
    // choice would silently route to the default warehouse.
    if (!form.warehouse_id) {
      toast({ title: 'Warehouse is required', description: 'Pick which warehouse this shipment lands in.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        vehicle_number: form.vehicle_number,
        expected_delivery_date: form.expected_delivery_date,
        production_date: form.production_date,
        expiry_date: form.expiry_date,
        hold_pallet: !!form.hold_pallet,
        notes: form.notes,
        customer_name: form.customer_name,
        container_no: form.container_no,
        warehouse_id: form.warehouse_id,
        line_items: cleanLines,
      };
      if (editingAsnId) {
        await axios.put(`${API}/admin/asn/${editingAsnId}`, payload, { headers: authHeaders });
        toast({ title: 'ASN updated' });
      } else {
        // Feb 2026 v3 — auto-open the Tally Sheet in a new tab
        // immediately after create so the warehouse gets its paper
        // form without an extra click. Falls back gracefully if the
        // browser pop-up blocks (user can still Print from the card).
        const { data } = await axios.post(
          `${API}/admin/asn`,
          { ...payload, status: 'expected' },
          { headers: authHeaders },
        );
        toast({ title: 'ASN created', description: 'Tally Sheet opening — ready for the truck to arrive.' });
        if (data?.id) openTallySheet(data.id);
      }
      setModalOpen(false);
      setEditingAsnId(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Feb 2026 v6 — "Mark Received" button retired from admin. Receiving
  // now flows exclusively through the assigned receiver in Floor Mode →
  // Receiving (scan-per-item → Complete Receipt). The `/admin/asn/{id}/
  // receive` endpoint stays for backwards compatibility but the UI no
  // longer surfaces it.

  const assignReceiver = async (a, receiverEmail) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${a.id}/assign-receiver`,
        { email: receiverEmail || '' },
        { headers: authHeaders },
      );
      toast({
        title: receiverEmail ? 'Receiver assigned' : 'Receiver cleared',
        description: receiverEmail ? data.receiver_name || receiverEmail : undefined,
      });
      // Local patch so the card refreshes without a full reload.
      setAsns((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...data } : x)));
    } catch (err) {
      toast({ title: 'Assign failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  const setAutoPutaway = async (a, enabled, pickerEmail) => {
    try {
      const { data } = await axios.put(
        `${API}/admin/asn/${a.id}/auto-putaway`,
        { enabled, email: pickerEmail || '' },
        { headers: authHeaders },
      );
      toast({
        title: enabled ? 'Auto put-away enabled' : 'Auto put-away disabled',
        description: enabled ? `Every new pallet → ${data.auto_putaway_name || pickerEmail}` : undefined,
      });
      setAsns((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...data } : x)));
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  // 2026-10 bugfix — mirrors FloorTasks.jsx's `completeReceipt`
  // against the same `/complete-receipt` endpoint, including the
  // full-scan-gate 409 → partial-receipt confirm flow. Lets an admin
  // finish a fully-scanned ASN without needing the receiver to go
  // back into Floor Mode just to click one button.
  const completeReceiptAdmin = async (a, opts = {}) => {
    const finalizeAsPartial = !!opts.finalizeAsPartial;
    if (!finalizeAsPartial) {
      if (!window.confirm(`Complete receipt for ${a.asn_number}? The ASN will flip to Received and any qty variance will be logged.`)) return;
    }
    setCompletingReceiptId(a.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${a.id}/complete-receipt`,
        finalizeAsPartial
          ? { finalize_as_partial: true, partial_note: opts.partialNote || '' }
          : {},
        { headers: authHeaders },
      );
      toast({
        title: data.finalized_as_partial ? 'ASN received (partial)' : 'ASN received',
        description: (data.variance || []).length
          ? `${data.scans_count} scans · ${data.variance.length} line(s) with variance`
          : `${data.scans_count} scans committed`,
      });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'incomplete_scan') {
        const lines = (detail.shortfall_lines || []).map((l) =>
          `  • ${l.sku || l.product_id}: expected ${l.expected_qty}, scanned ${l.actual_qty} (short ${Math.abs(l.diff)})`,
        ).join('\n');
        const ok = window.confirm(
          `${detail.shortfall_lines?.length || 0} line(s) still short:\n\n${lines}\n\n` +
          `Close this ASN as a PARTIAL receipt and keep it open for the missing qty via "Reopen"?`,
        );
        if (ok) {
          const note = window.prompt('Optional note for audit (why partial?):', '') || '';
          setCompletingReceiptId(null);
          return completeReceiptAdmin(a, { finalizeAsPartial: true, partialNote: note });
        }
      } else {
        toast({ title: 'Complete Receipt failed', description: detail || 'Try again.' });
      }
    } finally {
      setCompletingReceiptId(null);
    }
  };

  const releaseHold = async (a) => {
    if (!window.confirm(`Release Hold Pallet on ${a.asn_number}?\n\nAllocated FEFO will start picking from this stock immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/asn/${a.id}/release-hold`, {}, { headers: authHeaders });
      toast({ title: 'Hold released', description: `${data.released || 0} placement(s) unblocked.` });
      load();
    } catch (err) {
      toast({ title: 'Release failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  // Feb 2026 — Super-admin hold on ANY ASN status. The hold blocks
  // allocation + picking + dispatch of its stock. Put-away and
  // receiving are intentionally unaffected.
  const holdAsn = async (a) => {
    const reason = window.prompt(
      `Place ${a.asn_number} on hold?\n\n`
      + `This blocks allocation, picking, and dispatch of every pallet from this ASN until a super admin releases the hold.\n\n`
      + `Reason (optional, visible to the floor):`,
      '',
    );
    if (reason === null) return; // user cancelled
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${a.id}/hold`,
        { reason: (reason || '').trim() },
        { headers: authHeaders },
      );
      toast({
        title: 'ASN on hold',
        description: `${data.placements_held || 0} placement(s) blocked.`,
      });
      load();
    } catch (err) {
      toast({ title: 'Hold failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  const cancelAsn = async (a) => {
    if (!window.confirm(`Cancel ${a.asn_number}? This is only possible for draft/expected ASNs.`)) return;
    try {
      await axios.post(`${API}/admin/asn/${a.id}/cancel`, {}, { headers: authHeaders });
      toast({ title: 'ASN cancelled' });
      load();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  const deleteAsn = async (a) => {
    // Feb 2026 v9 — super-admin escape hatch: an extra explicit
    // confirm on `expected` / `received` ASNs since those cascade
    // to spawned placements + put-away tasks.
    let msg = `Delete ${a.asn_number} (${a.status})? This cannot be undone.`;
    if (a.status === 'received' || a.status === 'expected') {
      msg = `⚠️ DANGER — Delete ${a.asn_number} (status: ${a.status.toUpperCase()})?\n\n` +
            `This will hard-delete the ASN AND cascade-delete every placement + put-away task spawned from it. ` +
            `Stock landed by those placements will vanish from the warehouse.\n\n` +
            `Are you absolutely sure?`;
    }
    if (!window.confirm(msg)) return;
    try {
      const { data } = await axios.delete(`${API}/admin/asn/${a.id}`, { headers: authHeaders });
      const bits = [];
      if (data.placements_deleted) bits.push(`${data.placements_deleted} placement(s)`);
      if (data.tasks_deleted) bits.push(`${data.tasks_deleted} put-away task(s)`);
      toast({
        title: 'ASN deleted',
        description: bits.length ? `Cascade removed ${bits.join(' + ')}.` : undefined,
      });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  // Feb 2026 — Super-admin backfill for historical ASNs whose scans
  // pre-date the batch-write bugfix. Idempotent — safe to click more
  // than once; server skips scans that already have a matching
  // `stock_events` row.
  const resyncStock = async (a) => {
    if (!window.confirm(
      `Resync stock from ${a.asn_number}?\n\n` +
      `This re-applies every receipt scan onto products.batches / stock_qty ` +
      `and writes matching In/Out ledger rows (pallet + qty). Safe to run — ` +
      `already-applied scans are skipped.`
    )) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${a.id}/resync-stock`, {},
        { headers: authHeaders },
      );
      toast({
        title: 'Resync complete',
        description: `Applied ${data.applied} scan(s); skipped ${data.skipped}.`,
      });
      load();
    } catch (err) {
      toast({ title: 'Resync failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  // 2026-02 — Super-admin: Reopen a received ASN so the floor can
  // scan the remaining lines. Preserves every scan, placement,
  // put-away task and stock_events row. The subsequent Complete-
  // Receipt call will skip the already-posted scans via the backend
  // idempotency guard, so stock cannot double-post.
  const reopenAsn = async (a) => {
    const reason = window.prompt(
      `Reopen ${a.asn_number}?\n\n` +
      `This flips the ASN back to Expected so the floor can scan the ` +
      `remaining lines. All existing scans, placements, put-away tasks ` +
      `and stock events are preserved; the next Complete-Receipt will ` +
      `only post stock for NEW scans.\n\n` +
      `Reason (required for audit):`,
      '',
    );
    if (reason === null) return;  // cancelled
    const trimmed = reason.trim();
    if (!trimmed) {
      toast({ title: 'Reason required', description: 'Reopen requires a non-empty reason for audit.' });
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${a.id}/reopen`,
        { reason: trimmed },
        { headers: authHeaders },
      );
      toast({
        title: 'ASN reopened',
        description:
          `Preserved ${data.preserved.receipt_scans} scan(s), ` +
          `${data.preserved.placements} placement(s), ` +
          `${data.preserved.putaway_tasks} task(s), ` +
          `${data.preserved.stock_events} stock event(s). Floor can now continue scanning.`,
      });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({
        title: 'Reopen failed',
        description: typeof detail === 'string' ? detail : detail?.message || 'Try again.',
      });
    }
  };

  // Feb 2026 — Put-away task assignment (received ASNs). Fetches the
  // linked tasks the first time an ASN card expands (or a status
  // changes) and caches them under `asnTasks[asnId]`.
  const loadAsnTasks = async (asnId) => {
    try {
      const { data } = await axios.get(`${API}/admin/asn/${asnId}/putaway-tasks`, { headers: authHeaders });
      setAsnTasks((prev) => ({ ...prev, [asnId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      // Silent — the UI just renders "No tasks yet" until reload.
    }
  };

  // Feb 2026 — Single-ASN re-fetch used to hydrate the expanded card
  // with the latest `receipt_scans` and status BITs after floor
  // scans or admin actions land. Cheaper than a full list reload +
  // preserves scroll / sub-panel state.
  const refreshAsn = React.useCallback(async (asnId) => {
    if (!asnId) return null;
    try {
      const { data } = await axios.get(
        `${API}/admin/asn/${asnId}`,
        { headers: authHeaders },
      );
      if (data && data.id) {
        setAsns((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      }
      return data;
    } catch (err) {
      return null;
    }
  }, [authHeaders]);

  const assignAsnTask = async (asnId, taskId, assigneeEmail) => {
    try {
      await axios.post(
        `${API}/admin/asn/${asnId}/assign-task`,
        { task_id: taskId, assignee_email: assigneeEmail || '' },
        { headers: authHeaders },
      );
      toast({
        title: assigneeEmail ? 'Task assigned' : 'Task unassigned',
        description: assigneeEmail || undefined,
      });
      loadAsnTasks(asnId);
      refreshAsn(asnId);
    } catch (err) {
      toast({ title: 'Assign failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  // Feb 2026 — Bulk-assign every open put-away task on the ASN in one
  // round-trip. `assigneeEmail=""` clears open assignments.
  const assignAllAsnTasks = async (asnId, assigneeEmail) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/asn/${asnId}/assign-all-tasks`,
        { assignee_email: assigneeEmail || '' },
        { headers: authHeaders },
      );
      const bits = [];
      bits.push(`${data.assigned} task${data.assigned === 1 ? '' : 's'} ${assigneeEmail ? 'assigned' : 'unassigned'}`);
      if (data.skipped_completed) bits.push(`${data.skipped_completed} already completed`);
      toast({
        title: assigneeEmail ? 'All open put-away tasks assigned' : 'All open put-away tasks cleared',
        description: bits.join(' · '),
      });
      loadAsnTasks(asnId);
      refreshAsn(asnId);
    } catch (err) {
      toast({ title: 'Bulk assign failed', description: err.response?.data?.detail || 'Try again.' });
    }
  };

  const toggleExpand = (a) => {
    const isOpen = expandedId === a.id;
    setExpandedId(isOpen ? null : a.id);
    if (!isOpen) {
      // Feb 2026 — Always re-hydrate the ASN on expand so scans /
      // put-away results the floor picker added since page load are
      // visible immediately.
      refreshAsn(a.id);
      if (a.status === 'received' && !asnTasks[a.id]) {
        loadAsnTasks(a.id);
      }
    }
  };

  // Feb 2026 — While an ASN is expanded, poll `GET /admin/asn/{id}`
  // every 30s (was 8s) so scans posted by the floor app + put-aways
  // completed by pickers show up without a full page reload. Pauses
  // while the browser tab is backgrounded (visibilityState !== visible)
  // so hidden tabs don't hammer the API.
  React.useEffect(() => {
    if (!expandedId) return undefined;
    let timer = null;
    const start = () => {
      if (timer != null) return;
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshAsn(expandedId);
        }
      }, 30000);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [expandedId, refreshAsn]);

  // Feb 2026 — Paginated "load more". Appends the next PAGE_SIZE ASNs
  // onto `asns` without re-fetching the already-loaded page.
  const loadMoreAsns = async () => {
    if (asnLoadingMore) return;
    if (asns.length >= asnTotal) return;
    setAsnLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/admin/asn`, {
        headers: authHeaders,
        params: { limit: PAGE_SIZE, offset: asnOffset },
      });
      const items = Array.isArray(data) ? data : (data?.items || []);
      const total = Array.isArray(data) ? asns.length + items.length : (data?.total || 0);
      setAsns((prev) => [...prev, ...items]);
      setAsnTotal(total);
      setAsnOffset(asnOffset + items.length);
    } catch (err) {
      toast({ title: 'Load more failed', description: err.response?.data?.detail || 'Try again.' });
    } finally {
      setAsnLoadingMore(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display flex items-center gap-2">
              <PackagePlus className="w-7 h-7 text-[#0078D4]" />
              Goods Receiving
            </h1>
            <p className="text-neutral-500 mt-1">Log an inbound shipment before the truck arrives · items land in Stage on receipt and spawn a Put-away task per line.</p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold text-sm px-4 py-2 shadow-sm transition"
              data-testid="asn-new-btn"
            >
              <Plus className="w-4 h-4" />
              New ASN
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ASN #, vehicle #, or item code…"
              className="w-full rounded-full border border-neutral-300 bg-white pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
              data-testid="asn-search-input"
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 p-1 rounded-full"
                aria-label="Clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2" role="tablist">
            {['all', 'draft', 'expected', 'received', 'cancelled', 'hold'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${
                  statusFilter === s ? 'bg-[#0F172A] text-white' : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                }`}
                data-testid={`asn-filter-${s}`}
              >
                {s} ({
                  s === 'all'
                    ? asns.length
                    : s === 'hold'
                      ? asns.filter((a) => a.hold_pallet).length
                      : asns.filter((a) => a.status === s).length
                })
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-neutral-500 p-6">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center" data-testid="asn-empty">
            <PackagePlus className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500 text-sm">No ASNs yet. Click <b>New ASN</b> to log an incoming shipment.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="asn-list">
            {filtered.map((a) => {
              const isOpen = expandedId === a.id;
              const editable = a.status === 'draft' || a.status === 'expected';
              // Feb 2026 v2 — Edit is available on cancelled ASNs too
              // (super admin's "fix a mistake" path). Delete is safe on
              // draft AND cancelled (no downstream placements to
              // orphan). Received ASNs are still protected.
              const canEditThis  = canEdit   && (editable || a.status === 'cancelled');
              // Feb 2026 v9 — Super admin can delete an ASN in any
              // status (cascade-deletes spawned placements + put-away
              // tasks server-side). Sub-admins still restricted to
              // draft / cancelled.
              const canDeleteThis = canDelete && (isSuper || a.status === 'draft' || a.status === 'cancelled');
              // Feb 2026 — Hold flag is now visible at ANY status.
              // Super admin can hold/unhold an ASN regardless of
              // whether it's still expected, received, or cancelled.
              const isHeld = !!a.hold_pallet;
              return (
                <div
                  key={a.id}
                  className={`bg-white rounded-2xl border overflow-hidden ${isHeld ? 'border-amber-300 ring-1 ring-amber-200' : 'border-neutral-200'}`}
                  data-testid={`asn-card-${a.asn_number}`}
                >
                  <div
                    className="p-4 flex flex-wrap items-center gap-4 cursor-pointer hover:bg-neutral-50"
                    onClick={() => toggleExpand(a)}
                  >
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-[#0078D4]" data-testid={`asn-number-${a.asn_number}`}>
                          {a.asn_number}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[a.status] || 'bg-neutral-200'}`}>
                          {a.status}
                        </span>
                        {a.hold_pallet && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
                            data-testid={`asn-hold-badge-${a.asn_number}`}
                            title={
                              a.hold_reason
                                ? `Held: ${a.hold_reason}${a.held_by ? ` · by ${a.held_by}` : ''}`
                                : (a.held_by ? `Held by ${a.held_by}` : 'On hold')
                            }
                          >
                            <Lock className="w-3 h-3" /> Hold
                          </span>
                        )}
                        {/* Feb 2026 — Receive + Put-away progress
                            chips. Receive is derived from
                            `receipt_scans` vs `line_items`; put-away
                            comes from the thin flags endpoint. */}
                        {(() => {
                          const rf = computeReceivedFlag(a);
                          if (rf === 'fully') {
                            return (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                                data-testid={`asn-receive-chip-${a.asn_number}`}
                                title="Scanned qty meets or exceeds declared qty on every line."
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                                Fully Received
                              </span>
                            );
                          }
                          if (rf === 'partial') {
                            return (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                                data-testid={`asn-receive-chip-${a.asn_number}`}
                                title="Some lines scanned but not every declared qty is met yet."
                              >
                                <span className="relative inline-flex h-1.5 w-1.5" aria-hidden="true">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600" />
                                </span>
                                Partial Received
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {putawayFlags[a.asn_number] === 'fully' && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                            data-testid={`asn-putaway-chip-${a.asn_number}`}
                            title="Every pallet from this ASN is in a final location (stage = false)."
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                            Fully Put away
                          </span>
                        )}
                        {putawayFlags[a.asn_number] === 'partial' && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                            data-testid={`asn-putaway-chip-${a.asn_number}`}
                            title="Some pallets are still in staging — mix of staged and placed."
                          >
                            <span className="relative inline-flex h-1.5 w-1.5" aria-hidden="true">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-600" />
                            </span>
                            Partial Put away
                          </span>
                        )}
                        {/* Feb 2026 — Warehouse chip. Resolves the
                            ASN's warehouse_id against the loaded
                            list so ops can spot a mis-routed
                            shipment at a glance without opening
                            the card. Falls back to "Unassigned"
                            for legacy ASNs saved before the field
                            was mandatory. */}
                        {(() => {
                          const wh = warehouses.find((w) => w.id === a.warehouse_id);
                          const label = wh?.name || (a.warehouse_id ? a.warehouse_id.slice(0, 8) : 'Unassigned');
                          const missing = !a.warehouse_id;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                                missing
                                  ? 'bg-neutral-100 text-neutral-500 border-neutral-200'
                                  : 'bg-sky-50 text-sky-700 border-sky-200'
                              }`}
                              data-testid={`asn-warehouse-badge-${a.asn_number}`}
                              title={missing ? 'No warehouse set (legacy ASN)' : `Target warehouse: ${label}`}
                            >
                              <WarehouseIcon className="w-3 h-3" />
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        {a.vehicle_number && (
                          <span className="inline-flex items-center gap-1"><Truck className="w-3 h-3" /> {a.vehicle_number}</span>
                        )}
                        {a.expected_delivery_date && (
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> ETA {a.expected_delivery_date}</span>
                        )}
                        <span>· created {formatDateTime(a.created_at)}</span>
                        {a.received_at && (
                          <span className="inline-flex items-center gap-1 text-emerald-700">· received {formatDateTime(a.received_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-neutral-600 tabular-nums">
                      {(a.line_items || []).length} lines · {(a.line_items || []).reduce((s, l) => s + Number(l.qty || 0), 0)} units
                    </div>
                    <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {isOpen && (
                    <div className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-4" data-testid={`asn-details-${a.asn_number}`}>
                      {/* Feb 2026 — Activity-by chips. Short first
                          names for Created / Received / Released
                          from hold / Scanned / Put-Away. Rendered at
                          the TOP of the detail header so ops sees
                          who touched this ASN at a glance. */}
                      <AsnActivityByChips activity={a.activity_by} asnNumber={a.asn_number} />
                      {/* Meta grid */}
                      <div className="grid sm:grid-cols-2 gap-4 mb-4 text-xs">
                        <div>
                          <div className="text-neutral-500">Vehicle</div>
                          <div className="font-semibold text-[#0F172A]">{a.vehicle_number || '—'}</div>
                        </div>
                        <div>
                          <div className="text-neutral-500">Expected delivery</div>
                          <div className="font-semibold text-[#0F172A]">{a.expected_delivery_date || '—'}</div>
                        </div>
                        <div>
                          <div className="text-neutral-500">Default production date</div>
                          <div className="font-semibold text-[#0F172A]">{a.production_date || '—'}</div>
                        </div>
                        <div>
                          <div className="text-neutral-500">Default expiry date</div>
                          <div className="font-semibold text-[#0F172A]">{a.expiry_date || '—'}</div>
                        </div>
                        {/* Feb 2026 — Top-level Expected Qty tile. Editable
                            inline for users with `asn.edit_expected_qty`
                            when the ASN is in Expected status or on Hold.
                            Everyone else sees it as read-only. */}
                        {(() => {
                          const canHeaderEdit = canEditExpQty && (a.status === 'expected' || a.hold_pallet);
                          const isHeaderEditing = headerQtyEditId === a.id;
                          const displayVal = (a.expected_qty === null || a.expected_qty === undefined || a.expected_qty === '')
                            ? '—'
                            : String(a.expected_qty);
                          const commitHeader = async () => {
                            const raw = String(headerQtyDraft || '').trim();
                            let next;
                            if (raw === '') {
                              next = null;
                            } else {
                              const n = Number(raw);
                              if (!Number.isFinite(n) || n < 0) {
                                toast({ title: 'Enter a non-negative number', variant: 'destructive' });
                                return;
                              }
                              next = n;
                            }
                            const prev = (a.expected_qty === null || a.expected_qty === undefined)
                              ? null
                              : Number(a.expected_qty);
                            if ((prev === null && next === null) || (prev !== null && next !== null && Math.abs(prev - next) < 1e-9)) {
                              setHeaderQtyEditId(null);
                              return;
                            }
                            setHeaderQtySaving(true);
                            try {
                              await axios.patch(
                                `${API}/admin/asn/${a.id}/expected-qty`,
                                { qty: next },
                                { headers: authHeaders },
                              );
                              toast({
                                title: 'Expected Qty updated',
                                description: `ASN ${a.asn_number}: ${displayVal} → ${next === null ? '—' : next}`,
                              });
                              setHeaderQtyEditId(null);
                              await load();
                            } catch (err) {
                              toast({
                                title: 'Could not save',
                                description: err?.response?.data?.detail || err?.message,
                                variant: 'destructive',
                              });
                            } finally {
                              setHeaderQtySaving(false);
                            }
                          };
                          return (
                            <div data-testid={`asn-header-expected-qty-${a.asn_number}`}>
                              <div className="text-neutral-500 flex items-center gap-1">
                                Expected Qty
                                {!canHeaderEdit && canEditExpQty && (a.status === 'received' || a.status === 'draft' || a.status === 'cancelled') && (
                                  <span className="text-[9px] text-neutral-400 normal-case">(locked — status)</span>
                                )}
                              </div>
                              {isHeaderEditing ? (
                                <div className="inline-flex items-center gap-1 mt-0.5">
                                  <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    autoFocus
                                    value={headerQtyDraft}
                                    onChange={(e) => setHeaderQtyDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); commitHeader(); }
                                      if (e.key === 'Escape') setHeaderQtyEditId(null);
                                    }}
                                    disabled={headerQtySaving}
                                    className="w-24 h-7 px-2 rounded border border-sky-400 text-xs font-semibold tabular-nums focus:outline-none focus:ring-1 focus:ring-sky-400"
                                    data-testid={`asn-header-expected-qty-input-${a.asn_number}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={commitHeader}
                                    disabled={headerQtySaving}
                                    className="text-[10px] font-bold text-white bg-[#0078D4] hover:bg-[#005A9E] rounded px-2 py-1 disabled:opacity-50"
                                    data-testid={`asn-header-expected-qty-save-${a.asn_number}`}
                                  >
                                    {headerQtySaving ? '…' : 'Save'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setHeaderQtyEditId(null)}
                                    disabled={headerQtySaving}
                                    className="text-[10px] font-semibold text-neutral-600 hover:text-neutral-900 px-1 disabled:opacity-50"
                                    data-testid={`asn-header-expected-qty-cancel-${a.asn_number}`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className="font-semibold text-[#0F172A] tabular-nums"
                                    data-testid={`asn-header-expected-qty-value-${a.asn_number}`}
                                  >
                                    {displayVal}
                                  </span>
                                  {canHeaderEdit && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setHeaderQtyEditId(a.id);
                                        setHeaderQtyDraft(
                                          (a.expected_qty === null || a.expected_qty === undefined)
                                            ? ''
                                            : String(a.expected_qty),
                                        );
                                      }}
                                      className="text-[#0078D4] hover:text-[#005A9E] p-0.5"
                                      title="Edit Expected Qty"
                                      data-testid={`asn-header-expected-qty-edit-${a.asn_number}`}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {a.notes && (
                        <div className="text-xs text-neutral-600 mb-3">
                          <span className="text-neutral-500">Notes:</span> {a.notes}
                        </div>
                      )}
                      {/* Line items */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                              <th className="py-1.5 pr-3 font-semibold w-10 text-right tabular-nums">#</th>
                              <th className="py-1.5 pr-3 font-semibold">Item code</th>
                              <th className="py-1.5 pr-3 font-semibold">Name</th>
                              <th className="py-1.5 pr-3 font-semibold text-right">Qty</th>
                              <th className="py-1.5 pr-3 font-semibold">UOM</th>
                              <th className="py-1.5 pr-3 font-semibold">Prod. date</th>
                              <th className="py-1.5 pr-3 font-semibold">Exp. date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-200">
                            {(a.line_items || []).map((ln, i) => {
                              const editKey = `${a.id}:${i}`;
                              const isEditing = qtyEditKey === editKey;
                              const lineNo = (ln.line_no != null && ln.line_no !== '') ? ln.line_no : (i + 1);
                              // Oct 2026 — Expected Qty inline edit.
                              // Allowed only when the ASN is still editable
                              // (not received), the user has the dedicated
                              // `asn.edit_expected_qty` slug (or is super
                              // admin — handled server-side).
                              const canInlineQty = canEditExpQty && (a.status !== 'received' || a.hold_pallet);
                              // Feb 2026 — Post-receive edit. Super admin
                              // OR the original picker (`received_by`
                              // matches current user). Server re-checks.
                              const receivedBy = (a.received_by || '').trim().toLowerCase();
                              const canPostReceiveEdit =
                                a.status === 'received' &&
                                (isSuper || (!!currentEmail && currentEmail === receivedBy));
                              const saveQty = async () => {
                                const next = Number(qtyDraft);
                                if (!Number.isFinite(next) || next <= 0) {
                                  toast({ title: 'Qty must be greater than zero', variant: 'destructive' });
                                  return;
                                }
                                if (Math.abs(next - Number(ln.qty || 0)) < 1e-9) {
                                  setQtyEditKey(null);
                                  return;
                                }
                                setQtySaving(true);
                                try {
                                  await axios.patch(`${API}/admin/asn/${a.id}/line/${i}/qty`, { line_index: i, qty: next }, { headers: authHeaders });
                                  toast({ title: 'Expected qty updated', description: `${ln.sku || ln.name}: ${ln.qty} → ${next}` });
                                  setQtyEditKey(null);
                                  await load();
                                } catch (e) {
                                  toast({ title: 'Could not save', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
                                } finally {
                                  setQtySaving(false);
                                }
                              };
                              return (
                                <tr key={`${a.id}-${i}`} data-testid={`asn-line-row-${a.id}-${i}`}>
                                  <td className="py-1.5 pr-3 text-right tabular-nums text-neutral-500" data-testid={`asn-line-no-${a.id}-${i}`}>{lineNo}</td>
                                  <td className="py-1.5 pr-3 font-mono text-[#0078D4]">{ln.sku || '—'}</td>
                                  <td className="py-1.5 pr-3 text-[#0F172A]">{ln.name}</td>
                                  <td className="py-1.5 pr-3 text-right tabular-nums">
                                    {isEditing ? (
                                      <div className="inline-flex items-center gap-1 justify-end">
                                        <input
                                          type="number"
                                          min="1"
                                          step="any"
                                          autoFocus
                                          value={qtyDraft}
                                          onChange={(e) => setQtyDraft(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); saveQty(); }
                                            if (e.key === 'Escape') setQtyEditKey(null);
                                          }}
                                          disabled={qtySaving}
                                          className="w-24 rounded-md border border-[#0078D4] px-2 py-0.5 text-right tabular-nums text-xs focus:outline-none focus:ring-1 focus:ring-[#0078D4]"
                                          data-testid={`asn-exp-qty-input-${a.id}-${i}`}
                                        />
                                        <button
                                          type="button"
                                          onClick={saveQty}
                                          disabled={qtySaving}
                                          className="text-[#0078D4] hover:bg-blue-50 rounded p-0.5"
                                          data-testid={`asn-exp-qty-save-${a.id}-${i}`}
                                          aria-label="Save"
                                        >
                                          <Check className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setQtyEditKey(null)}
                                          disabled={qtySaving}
                                          className="text-neutral-400 hover:text-red-600 rounded p-0.5"
                                          data-testid={`asn-exp-qty-cancel-${a.id}-${i}`}
                                          aria-label="Cancel"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ) : canInlineQty ? (
                                      <button
                                        type="button"
                                        onClick={() => { setQtyEditKey(editKey); setQtyDraft(String(ln.qty || '')); }}
                                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-blue-50 hover:text-[#0078D4] tabular-nums"
                                        title="Edit Expected Qty"
                                        data-testid={`asn-exp-qty-trigger-${a.id}-${i}`}
                                      >
                                        <Pencil className="w-3 h-3 opacity-50" />
                                        {ln.qty}
                                      </button>
                                    ) : canPostReceiveEdit ? (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setPostReceiveEdit({ asn: a, line: ln, lineIndex: i });
                                          setPostReceiveQty(String(ln.qty || ''));
                                          setPostReceiveReason('');
                                        }}
                                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-amber-50 hover:text-amber-700 tabular-nums"
                                        title="Edit Expected Qty (post-receive — audited)"
                                        data-testid={`asn-post-receive-qty-trigger-${a.id}-${i}`}
                                      >
                                        <Pencil className="w-3 h-3 opacity-60 text-amber-600" />
                                        {ln.qty}
                                      </button>
                                    ) : (
                                      ln.qty
                                    )}
                                  </td>
                                  <td className="py-1.5 pr-3 text-neutral-600">{ln.uom}</td>
                                  <td className="py-1.5 pr-3 text-neutral-600">{ln.production_date || a.production_date || '—'}</td>
                                  <td className="py-1.5 pr-3 text-neutral-600">{ln.expiry_date || a.expiry_date || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {/* Feb 2026 v4 — Receipt Scans (Floor Mode) */}
                      {(a.receipt_scans || []).length > 0 && (
                        <div className="mt-4 rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid={`asn-receipt-scans-${a.asn_number}`}>
                          <div className="px-3 py-2 border-b border-neutral-100 bg-neutral-50 flex items-center justify-between gap-2">
                            <div className="text-[11px] uppercase tracking-wider font-bold text-[#0F172A]">
                              Receipt Scans ({a.receipt_scans.length})
                            </div>
                            <div className="text-[10px] text-neutral-500 tabular-nums">
                              Scanned {(a.receipt_scans || []).reduce((s, r) => s + Number(r.qty || 0), 0)} · Expected {(a.line_items || []).reduce((s, l) => s + Number(l.qty || 0), 0)}
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                                  <th className="py-1.5 pr-3 font-semibold pl-3">Item #</th>
                                  <th className="py-1.5 pr-3 font-semibold">Product</th>
                                  <th className="py-1.5 pr-3 font-semibold text-right">Qty</th>
                                  <th className="py-1.5 pr-3 font-semibold">Pallet #</th>
                                  <th className="py-1.5 pr-3 font-semibold">Exp</th>
                                  <th className="py-1.5 pr-3 font-semibold">Prod</th>
                                  <th className="py-1.5 pr-3 font-semibold">Scanned by</th>
                                  <th className="py-1.5 pr-3 font-semibold">Location</th>
                                  {isSuper && (
                                    <th className="py-1.5 pr-3 pl-3 font-semibold text-right">Actions</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-neutral-100">
                                {a.receipt_scans.map((s) => (
                                  <ReceiptScanRow
                                    key={s.id}
                                    scan={s}
                                    asnId={a.id}
                                    isSuper={isSuper}
                                    authHeaders={authHeaders}
                                    onChanged={load}
                                    staffOptions={assignableUsers}
                                  />
                                ))}
                              </tbody>
                              {/* Feb 2026 — Totals row under receiving
                                  quantities. Scanned sum (left) and
                                  variance vs declared Expected (right)
                                  so the receiver sees over/short at a
                                  glance. */}
                              {(() => {
                                const scannedSum = (a.receipt_scans || []).reduce((n, r) => n + Number(r.qty || 0), 0);
                                const expectedSum = (a.line_items || []).reduce((n, l) => n + Number(l.qty || 0), 0);
                                const diff = scannedSum - expectedSum;
                                const diffColor = diff === 0
                                  ? 'text-emerald-700'
                                  : (diff > 0 ? 'text-amber-700' : 'text-red-700');
                                const diffLabel = diff === 0
                                  ? 'matches expected'
                                  : (diff > 0 ? `+${diff} over` : `${diff} short`);
                                return (
                                  <tfoot
                                    className="bg-neutral-50 border-t-2 border-neutral-200"
                                    data-testid={`asn-receipt-scans-footer-${a.asn_number}`}
                                  >
                                    <tr className="text-[11px]">
                                      <td className="py-2 pl-3 pr-3 font-bold uppercase tracking-wider text-neutral-600" colSpan={2}>
                                        Total scanned
                                      </td>
                                      <td
                                        className="py-2 pr-3 text-right font-bold tabular-nums text-[#0F172A]"
                                        data-testid={`asn-receipt-scans-total-${a.asn_number}`}
                                      >
                                        {scannedSum}
                                      </td>
                                      <td className="py-2 pr-3 text-neutral-500" colSpan={isSuper ? 6 : 5}>
                                        <span className="text-neutral-500">
                                          Expected <span className="tabular-nums font-bold text-[#0F172A]">{expectedSum}</span>
                                        </span>
                                        <span className="mx-2 text-neutral-300">·</span>
                                        <span className={`font-bold tabular-nums ${diffColor}`}>
                                          {diffLabel}
                                        </span>
                                      </td>
                                    </tr>
                                  </tfoot>
                                );
                              })()}
                            </table>
                          </div>
                        </div>
                      )}
                      {/* Feb 2026 — Line Edit History (audit trail). Only
                          visible to super admins + sub-admins carrying
                          the `asn.view_edit_history` slug. Lists every
                          post-receive expected-qty edit with who, when,
                          old → new and the mandatory reason. */}
                      {(canViewEditHistory) && (a.line_qty_audit || []).length > 0 && (
                        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden" data-testid={`asn-edit-history-${a.asn_number}`}>
                          <div className="px-3 py-2 border-b border-amber-100 bg-amber-100/60 flex items-center justify-between gap-2">
                            <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800">
                              Line Edit History ({a.line_qty_audit.length})
                            </div>
                            <div className="text-[10px] text-amber-700">Post-receive expected-qty edits · stock untouched</div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-amber-700 border-b border-amber-200">
                                  <th className="py-1.5 pr-3 pl-3 font-semibold w-10 text-right tabular-nums">#</th>
                                  <th className="py-1.5 pr-3 font-semibold">Item</th>
                                  <th className="py-1.5 pr-3 font-semibold text-right">From</th>
                                  <th className="py-1.5 pr-3 font-semibold text-right">To</th>
                                  <th className="py-1.5 pr-3 font-semibold">Reason</th>
                                  <th className="py-1.5 pr-3 font-semibold">Edited by</th>
                                  <th className="py-1.5 pr-3 font-semibold">When</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-amber-100">
                                {a.line_qty_audit.map((aud, j) => (
                                  <tr key={`${a.id}-aud-${j}`} data-testid={`asn-edit-history-row-${a.id}-${j}`}>
                                    <td className="py-1.5 pr-3 pl-3 text-right tabular-nums text-neutral-500">{aud.line_no ?? (aud.line_index != null ? aud.line_index + 1 : '—')}</td>
                                    <td className="py-1.5 pr-3 text-neutral-800"><span className="font-mono text-[#0078D4]">{aud.sku || '—'}</span> {aud.name ? <span className="text-neutral-500">· {aud.name}</span> : null}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums text-neutral-600 line-through">{aud.old_qty}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums text-amber-800 font-semibold">{aud.new_qty}</td>
                                    <td className="py-1.5 pr-3 text-neutral-800">{aud.reason || '—'}</td>
                                    <td className="py-1.5 pr-3 text-neutral-600">{aud.by || '—'}</td>
                                    <td className="py-1.5 pr-3 text-neutral-500 whitespace-nowrap">{aud.at ? formatDateTime(aud.at) : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {/* Actions */}
                      <div className="mt-4 flex flex-wrap gap-2 justify-end">
                        {/* Feb 2026 v3 — Print Tally Sheet is available
                            on any status (draft/expected/received/
                            cancelled) so ops can reprint the paper
                            form even after receipt for audit. */}
                        <button
                          type="button"
                          onClick={() => openTallySheet(a.id)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-100 text-xs font-semibold px-3 py-1.5 transition"
                          data-testid={`asn-print-tally-btn-${a.asn_number}`}
                        >
                          <Printer className="w-3.5 h-3.5" /> Print Tally Sheet
                        </button>
                        {/* Feb 2026 — Stock Receipt: template-matching
                            PDF (via browser Print → Save as PDF)
                            unlocks as soon as the ASN is RECEIVED,
                            regardless of whether put-away is still in
                            progress or already finished. Receipt
                            describes WHAT came in, not where it ended
                            up. The backend uses the same gate so the
                            button + endpoint can't disagree. */}
                        {a.status === 'received' && (
                          <button
                            type="button"
                            onClick={() => openStockReceipt(a.id)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-stock-receipt-btn-${a.asn_number}`}
                            title="Print the Stock Receipt — available as soon as the ASN is received."
                          >
                            <Printer className="w-3.5 h-3.5" /> Stock Receipt
                          </button>
                        )}
                        {canEditThis && (
                          <button
                            type="button"
                            onClick={() => openEdit(a)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#0078D4] text-[#0078D4] bg-white hover:bg-[#DEECF9] text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-edit-btn-${a.asn_number}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            {a.status === 'cancelled' ? 'Edit & Reopen' : 'Edit'}
                          </button>
                        )}
                        {/* 2026-10 bugfix — Complete Receipt used to
                            exist ONLY in Floor Mode → Receiving, so an
                            ASN scanned 100% complete sat stuck in
                            `expected` forever if nobody went back to
                            the floor screen to click it (put-away ran
                            fine via Auto Put-Away, masking the stall).
                            Surface the same action here once there is
                            at least one scan, and explain scan
                            progress either way so "why can't I
                            complete/release this" is never a mystery. */}
                        {editable && canReceive && (() => {
                          const scans = a.receipt_scans || [];
                          const scannedQty = scans.reduce((s, r) => s + Number(r.qty || 0), 0);
                          const expectedQty = (a.line_items || []).reduce((s, l) => s + Number(l.qty || 0), 0);
                          if (scans.length === 0) {
                            return (
                              <span
                                className="text-[10px] text-neutral-500 italic self-center hidden sm:inline"
                                title="Assign a receiver below — they will scan the goods on the floor, then Complete Receipt unlocks here once scanning starts."
                              >
                                Receiving happens on the floor — no scans yet
                              </span>
                            );
                          }
                          const isComplete = completingReceiptId === a.id;
                          return (
                            <button
                              type="button"
                              disabled={isComplete}
                              onClick={() => completeReceiptAdmin(a)}
                              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 transition"
                              data-testid={`asn-complete-receipt-btn-${a.asn_number}`}
                              title={scannedQty >= expectedQty
                                ? 'Every expected line is fully scanned — flip this ASN to Received.'
                                : `Only ${scannedQty}/${expectedQty} units scanned so far — you can still close it as a partial receipt.`}
                            >
                              <Check className="w-3.5 h-3.5" />
                              {isComplete ? 'Completing…' : `Complete Receipt (${scannedQty}/${expectedQty})`}
                            </button>
                          );
                        })()}
                        {editable && canCancel && (
                          <button
                            type="button"
                            onClick={() => cancelAsn(a)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-red-300 text-red-700 bg-white hover:bg-red-50 text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-cancel-btn-${a.asn_number}`}
                          >
                            <Ban className="w-3.5 h-3.5" /> Cancel
                          </button>
                        )}
                        {canDeleteThis && (
                          <button
                            type="button"
                            onClick={() => deleteAsn(a)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-red-400 text-red-700 bg-white hover:bg-red-50 text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-delete-btn-${a.asn_number}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        )}
                        {isHeld && canHoldRelease && (
                          <button
                            type="button"
                            onClick={() => releaseHold(a)}
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-release-hold-btn-${a.asn_number}`}
                          >
                            <ShieldOff className="w-3.5 h-3.5" /> Release Hold
                          </button>
                        )}
                        {/* Feb 2026 — Super-admin hold action. Available
                            at ANY status (expected / received / put-away
                            in-progress / put-away completed / cancelled)
                            so phantom or contested stock can be
                            ring-fenced from allocation + dispatch at any
                            lifecycle stage. Receiving and put-away
                            workflows stay unaffected — only the FEFO
                            planner, picker, and dispatch gate honour
                            the hold flag. */}
                        {!isHeld && isSuper && (
                          <button
                            type="button"
                            onClick={() => holdAsn(a)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-amber-500 text-amber-700 bg-white hover:bg-amber-50 text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-hold-btn-${a.asn_number}`}
                            title="Place this ASN on hold — blocks allocation + picking + dispatch of its stock. Put-away keeps working."
                          >
                            <Lock className="w-3.5 h-3.5" /> Hold
                          </button>
                        )}
                        {/* Feb 2026 — Resync Stock: super-admin backfill
                            for historical ASNs whose receipts pre-date
                            the batch-write bugfix. Idempotent — server
                            skips scans that already have a matching
                            `stock_events` row. Visible on RECEIVED,
                            not-held ASNs only. */}
                        {isSuper && a.status === 'received' && !a.hold_pallet && (
                          <button
                            type="button"
                            onClick={() => resyncStock(a)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-sky-400 text-sky-700 bg-white hover:bg-sky-50 text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-resync-stock-btn-${a.asn_number}`}
                            title="Rebuild stock_qty + In/Out ledger rows from this ASN's scans (idempotent)."
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> Resync Stock
                          </button>
                        )}
                        {/* 2026-02 — Super-admin: Reopen a received ASN
                            so the floor can scan remaining lines. All
                            existing scans/placements/tasks/stock events
                            survive. The next Complete-Receipt skips
                            already-posted scans so stock cannot double. */}
                        {isSuper && a.status === 'received' && (
                          <button
                            type="button"
                            onClick={() => reopenAsn(a)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 text-amber-700 bg-white hover:bg-amber-50 text-xs font-semibold px-3 py-1.5 transition"
                            data-testid={`asn-reopen-btn-${a.asn_number}`}
                            title="Reopen this received ASN (preserves scans, placements, tasks, stock events)."
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Reopen
                          </button>
                        )}
                      </div>
                      {isHeld && (
                        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                          <span>
                            Stock has been received but is <b>held</b> — FEFO allocation will skip it until Release Hold is clicked.
                          </span>
                        </div>
                      )}
                      {/* Feb 2026 v6 — Receiver assignment (visible on open
                          ASNs) + Auto put-away toggle. The receiver is the
                          floor user who will scan goods; every scan writes
                          into `receipt_scans` and streams into the Receipt
                          Scans panel above in real time. Auto put-away
                          pre-stamps each spawned put-away task with a
                          chosen picker so nobody has to click per pallet. */}
                      {canAssign && (a.status === 'expected' || a.status === 'draft') && (
                        <div
                          className="mt-4 rounded-xl border border-neutral-200 bg-white p-3"
                          data-testid={`asn-receiver-section-${a.asn_number}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <PackagePlus className="w-4 h-4 text-emerald-600" />
                            <div className="text-[11px] uppercase tracking-wider font-bold text-[#0F172A]">
                              Assign receiving task
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-neutral-500">Receiver:</span>
                            <select
                              value={a.receiver_email || ''}
                              onChange={(e) => assignReceiver(a, e.target.value)}
                              className="rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                              data-testid={`asn-receiver-select-${a.asn_number}`}
                            >
                              <option value="">— Unassigned —</option>
                              {['picker', 'floor_incharge', 'manager', ''].map((r) => {
                                const label = r === 'picker' ? 'Pickers'
                                  : r === 'floor_incharge' ? 'Floor In-charge'
                                  : r === 'manager' ? 'Managers'
                                  : 'Other warehouse staff';
                                const rows = assignableUsers.filter((u) => (u.role || '') === r);
                                if (rows.length === 0) return null;
                                return (
                                  <optgroup key={r || 'other'} label={label}>
                                    {rows.map((u) => (
                                      <option key={u.email} value={u.email}>
                                        {u.name} ({u.email})
                                      </option>
                                    ))}
                                  </optgroup>
                                );
                              })}
                            </select>
                            {a.receiver_email && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {a.receiver_name || a.receiver_email}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-[10px] text-neutral-500">
                            The assigned receiver will see this ASN in Floor Mode → Receiving. Their scans stream into the <b>Receipt Scans</b> panel above in real time.
                          </div>
                        </div>
                      )}
                      {canAssign && (a.status === 'expected' || a.status === 'draft' || a.status === 'received') && (
                        <div
                          className="mt-3 rounded-xl border border-neutral-200 bg-white p-3"
                          data-testid={`asn-auto-putaway-section-${a.asn_number}`}
                        >
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <UserPlus className="w-4 h-4 text-[#0078D4]" />
                            <div className="text-[11px] uppercase tracking-wider font-bold text-[#0F172A] flex-1">
                              Auto put-away → picker
                            </div>
                            <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!a.auto_putaway_enabled}
                                onChange={(e) => setAutoPutaway(a, e.target.checked, a.auto_putaway_email || '')}
                                disabled={!a.auto_putaway_email && !a.auto_putaway_enabled}
                                className="w-4 h-4 accent-[#0078D4]"
                                data-testid={`asn-auto-putaway-toggle-${a.asn_number}`}
                              />
                              <span className={a.auto_putaway_enabled ? 'text-[#0078D4] font-bold' : 'text-neutral-600'}>
                                {a.auto_putaway_enabled ? 'ON' : 'OFF'}
                              </span>
                            </label>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="text-neutral-500">Auto-assign to:</span>
                            <select
                              value={a.auto_putaway_email || ''}
                              onChange={(e) => setAutoPutaway(a, a.auto_putaway_enabled && !!e.target.value, e.target.value)}
                              className="rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                              data-testid={`asn-auto-putaway-select-${a.asn_number}`}
                            >
                              <option value="">— Pick a picker —</option>
                              {['picker', 'floor_incharge', 'manager', ''].map((r) => {
                                const label = r === 'picker' ? 'Pickers'
                                  : r === 'floor_incharge' ? 'Floor In-charge'
                                  : r === 'manager' ? 'Managers'
                                  : 'Other warehouse staff';
                                const rows = assignableUsers.filter((u) => (u.role || '') === r);
                                if (rows.length === 0) return null;
                                return (
                                  <optgroup key={r || 'other'} label={label}>
                                    {rows.map((u) => (
                                      <option key={u.email} value={u.email}>
                                        {u.name} ({u.email})
                                      </option>
                                    ))}
                                  </optgroup>
                                );
                              })}
                            </select>
                          </div>
                          <div className="mt-1 text-[10px] text-neutral-500">
                            When ON, every pallet scanned into this ASN auto-creates a put-away task pre-assigned to the picker above — no per-pallet dropdown click needed.
                          </div>
                        </div>
                      )}
                      {/* Feb 2026 — Put-away tasks section. Only renders on
                          received ASNs (that's when tasks exist and put-away
                          assignment is enabled). For ASNs still in `expected`
                          or `draft` we surface a friendly hint so admins know
                          why the picker dropdown isn't there yet — put-away
                          assignment is only unlocked after Complete Receipt
                          (partial or full). */}
                      {a.status === 'received' ? (
                        <div
                          className="mt-4 rounded-xl border border-neutral-200 bg-white p-3"
                          data-testid={`asn-putaway-section-${a.asn_number}`}
                        >
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <UserPlus className="w-4 h-4 text-[#0078D4]" />
                            <div className="text-[11px] uppercase tracking-wider font-bold text-[#0F172A]">
                              Put-away tasks · assign to picker
                            </div>
                            {/* Feb 2026 — Bulk-assign dropdown: one
                                selection stamps EVERY open put-away
                                task on this ASN. Only shown when the
                                caller has assign rights and there's
                                at least one open task. */}
                            {canAssign && Array.isArray(asnTasks[a.id]) && asnTasks[a.id].some((t) => t.status !== 'completed') && (
                              <div className="ml-auto flex items-center gap-1.5 text-[11px]">
                                <span className="text-neutral-500 font-semibold">Assign all to:</span>
                                <select
                                  value=""
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '__clear__') { assignAllAsnTasks(a.id, ''); }
                                    else if (v) { assignAllAsnTasks(a.id, v); }
                                    e.target.value = ''; // reset dropdown so subsequent picks work
                                  }}
                                  className="rounded-lg border border-[#0078D4] bg-white px-2 py-1 text-[11px] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 font-semibold text-[#0078D4]"
                                  data-testid={`asn-putaway-assign-all-${a.asn_number}`}
                                >
                                  <option value="">— Pick a picker —</option>
                                  {isSuper && (
                                    <option value="qayum@mrpco.net">Super Admin (qayum@mrpco.net)</option>
                                  )}
                                  {['picker', 'floor_incharge', 'manager', ''].map((r) => {
                                    const label = r === 'picker' ? 'Pickers'
                                      : r === 'floor_incharge' ? 'Floor In-charge'
                                      : r === 'manager' ? 'Managers'
                                      : 'Other warehouse staff';
                                    const rows = assignableUsers.filter((u) => (u.role || '') === r);
                                    if (rows.length === 0) return null;
                                    return (
                                      <optgroup key={r || 'other'} label={label}>
                                        {rows.map((u) => (
                                          <option key={u.email} value={u.email}>
                                            {u.name} ({u.email})
                                          </option>
                                        ))}
                                      </optgroup>
                                    );
                                  })}
                                  <option value="__clear__">— Clear all open assignments —</option>
                                </select>
                                {/* Feb 2026 — Multi-picker split
                                    across contiguous location
                                    blocks. Opens the shared modal
                                    scoped to this ASN. */}
                                <button
                                  type="button"
                                  onClick={() => setSplitAsnId(a.id)}
                                  className="inline-flex items-center gap-1 rounded-lg border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-800 hover:bg-sky-100"
                                  data-testid={`asn-putaway-split-${a.asn_number}`}
                                  title="Split put-away tasks across multiple pickers by location"
                                >
                                  Split…
                                </button>
                              </div>
                            )}
                          </div>
                          {!asnTasks[a.id] ? (
                            <div className="text-xs text-neutral-500 py-2">Loading tasks…</div>
                          ) : asnTasks[a.id].length === 0 ? (
                            <div className="text-xs text-neutral-500 py-2">No put-away tasks linked to this ASN.</div>
                          ) : (
                            <div className="divide-y divide-neutral-100">
                              {asnTasks[a.id].map((t) => (
                                <div
                                  key={t.id}
                                  className="py-2 flex flex-wrap items-center gap-3"
                                  data-testid={`asn-putaway-task-${t.id}`}
                                >
                                  <div className="flex-1 min-w-[180px]">
                                    <div className="text-xs font-semibold text-[#0F172A] truncate">
                                      {t.product_name || t.product_id}
                                    </div>
                                    <div className="text-[10px] text-neutral-500 flex flex-wrap items-center gap-2">
                                      <span className="tabular-nums">{Number(t.qty || 0)} {t.uom || 'ctn'}</span>
                                      {t.pallet_id && <span>· {t.pallet_id}</span>}
                                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                        t.status === 'completed'
                                          ? 'bg-emerald-100 text-emerald-700'
                                          : t.status === 'in_progress'
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-neutral-200 text-neutral-700'
                                      }`}>
                                        {t.status}
                                      </span>
                                    </div>
                                  </div>
                                  {canAssign ? (
                                    <select
                                      value={t.assignee_email || ''}
                                      onChange={(e) => assignAsnTask(a.id, t.id, e.target.value)}
                                      disabled={t.status === 'completed'}
                                      className="rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 disabled:opacity-60 disabled:cursor-not-allowed"
                                      data-testid={`asn-putaway-assign-${t.id}`}
                                    >
                                      <option value="">— Unassigned —</option>
                                      {isSuper && (
                                        <option value="qayum@mrpco.net">Super Admin (qayum@mrpco.net)</option>
                                      )}
                                      {/* Feb 2026 v5 — group by role so pickers
                                          land at the top and floor_incharge /
                                          manager stay visually separated. */}
                                      {['picker', 'floor_incharge', 'manager', ''].map((r) => {
                                        const label = r === 'picker' ? 'Pickers'
                                          : r === 'floor_incharge' ? 'Floor In-charge'
                                          : r === 'manager' ? 'Managers'
                                          : 'Other warehouse staff';
                                        const rows = assignableUsers.filter((u) => (u.role || '') === r);
                                        if (rows.length === 0) return null;
                                        return (
                                          <optgroup key={r || 'other'} label={label}>
                                            {rows.map((u) => (
                                              <option key={u.email} value={u.email}>
                                                {u.name} ({u.email})
                                              </option>
                                            ))}
                                          </optgroup>
                                        );
                                      })}
                                    </select>
                                  ) : (
                                    <div className="text-xs text-neutral-600">
                                      {t.assignee_email ? (
                                        <span><span className="text-neutral-500">Assigned to </span><b>{t.assignee_name || t.assignee_email}</b></span>
                                      ) : (
                                        <span className="italic text-neutral-500">Unassigned</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {canAssign && assignableUsers.length === 0 && !isSuper && (
                            <div className="mt-2 text-[10px] text-neutral-500">
                              No warehouse users configured — grant the <span className="font-mono">warehouse</span> permission or a <span className="font-mono">picker</span> role to admin users to make them assignable.
                            </div>
                          )}
                        </div>
                      ) : (a.status === 'expected' || a.status === 'draft') && (
                        <div
                          className="mt-4 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-3 text-[11px] text-neutral-600 flex items-start gap-2"
                          data-testid={`asn-putaway-locked-${a.asn_number}`}
                        >
                          <UserPlus className="w-3.5 h-3.5 mt-0.5 text-neutral-400" />
                          <span>
                            <b>Put-away assignment locked.</b> The assigned receiver above must scan the goods on the floor and click <b>Complete Receipt</b> — pickers can be assigned per pallet once the ASN is closed. Turn on <b>Auto put-away</b> above to pre-assign every pallet automatically.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {asns.length < asnTotal && (
              <div className="pt-2 flex items-center justify-center" data-testid="asn-load-more-wrap">
                <button
                  type="button"
                  onClick={loadMoreAsns}
                  disabled={asnLoadingMore}
                  className="px-4 py-2 rounded-lg bg-white border border-neutral-300 text-sm font-semibold text-[#0F172A] hover:bg-neutral-50 disabled:opacity-50"
                  data-testid="asn-load-more-btn"
                >
                  {asnLoadingMore
                    ? 'Loading…'
                    : `Load more · showing ${asns.length} of ${asnTotal}`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* --- Create ASN modal --------------------------------------- */}
        {modalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            data-testid="asn-modal"
          >
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
              <div className="p-5 border-b border-neutral-100 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#0F172A]">{editingAsnId ? 'Edit ASN' : 'New ASN'}</h2>
                  <p className="text-xs text-neutral-500">{editingAsnId ? 'Update the shipment details before it lands. Received ASNs cannot be edited.' : 'Log an expected inbound shipment. Marks ready to receive once the truck lands.'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setModalOpen(false); setEditingAsnId(null); }}
                  className="p-2 rounded-full hover:bg-neutral-100 text-neutral-500"
                  aria-label="Close"
                  data-testid="asn-modal-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Header fields */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block sm:col-span-2">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Customer / Supplier name</span>
                    <input
                      type="text"
                      value={form.customer_name}
                      onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
                      placeholder="e.g. Al Bada Trading LLC"
                      className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                      data-testid="asn-customer-input"
                      autoComplete="off"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Vehicle number</span>
                    <input
                      type="text"
                      value={form.vehicle_number}
                      onChange={(e) => setForm((f) => ({ ...f, vehicle_number: e.target.value }))}
                      placeholder="OM-A9999"
                      className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                      data-testid="asn-vehicle-input"
                      autoComplete="off"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Container #</span>
                    <input
                      type="text"
                      value={form.container_no}
                      onChange={(e) => setForm((f) => ({ ...f, container_no: e.target.value }))}
                      placeholder="e.g. MSCU-1234567"
                      className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                      data-testid="asn-container-input"
                      autoComplete="off"
                    />
                  </label>
                  {/* Feb 2026 — mandatory Warehouse dropdown. Received
                      stock parks in this warehouse's STAGE zone. */}
                  <label className="block sm:col-span-2">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
                      Warehouse <span className="text-rose-500">*</span>
                    </span>
                    <select
                      value={form.warehouse_id || ''}
                      onChange={(e) => setForm((f) => ({ ...f, warehouse_id: e.target.value }))}
                      className={`mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4] ${
                        !form.warehouse_id ? 'border-rose-300' : 'border-neutral-300'
                      }`}
                      data-testid="asn-warehouse-select"
                      required
                    >
                      <option value="">— Select receiving warehouse —</option>
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id} data-testid={`asn-warehouse-option-${w.id}`}>
                          {w.name}
                          {w.is_default ? ' · default' : ''}
                        </option>
                      ))}
                    </select>
                    <span className="mt-1 block text-[10px] text-neutral-400 leading-tight">
                      Every scan for this ASN parks on the selected warehouse&apos;s <b>STAGE</b> zone.
                    </span>
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Expected delivery</span>
                    <input
                      type="date"
                      value={form.expected_delivery_date}
                      onChange={(e) => setForm((f) => ({ ...f, expected_delivery_date: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                      data-testid="asn-eta-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Default production date</span>
                    <input
                      type="date"
                      value={form.production_date}
                      onChange={(e) => setForm((f) => ({ ...f, production_date: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                      data-testid="asn-prod-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Default expiry date</span>
                    <input
                      type="date"
                      value={form.expiry_date}
                      onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                      data-testid="asn-exp-input"
                    />
                  </label>
                </div>

                {/* Hold Pallet toggle */}
                <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer" data-testid="asn-hold-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={!!form.hold_pallet}
                    onChange={(e) => setForm((f) => ({ ...f, hold_pallet: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    data-testid="asn-hold-toggle"
                  />
                  <div className="text-xs">
                    <div className="font-bold text-amber-900 uppercase tracking-wide">Hold Pallet</div>
                    <div className="text-amber-800 mt-0.5">Received stock will be marked <b>held</b> — FEFO allocation skips it until Release Hold is clicked on the ASN card.</div>
                  </div>
                </label>

                {/* Notes */}
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Notes (optional)</span>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Driver contact, temperature-controlled, off-hours delivery…"
                    className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                    data-testid="asn-notes-input"
                  />
                </label>

                {/* Line items */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Line items</div>
                  <div className="space-y-2">
                    {form.line_items.map((ln, idx) => {
                      const suggestions = productSuggestionsFor(idx);
                      const searchVal = lineSearch[idx] || '';
                      return (
                        <div
                          key={idx}
                          className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-3 space-y-2"
                          data-testid={`asn-line-${idx}`}
                        >
                          {ln.product_id ? (
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="font-mono font-bold text-[#0078D4] shrink-0" data-testid={`asn-line-sku-${idx}`}>{ln.sku || '—'}</span>
                                <span className="text-sm text-[#0F172A] truncate">{ln.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => patchLine(idx, { product_id: '', sku: '', name: '', size: '', uom: 'ctn' })}
                                className="text-xs text-neutral-500 hover:text-red-600 font-semibold"
                                data-testid={`asn-line-clear-${idx}`}
                              >
                                Change
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                              <input
                                type="text"
                                value={searchVal}
                                onChange={(e) => setLineSearch((prev) => ({ ...prev, [idx]: e.target.value }))}
                                placeholder="Search by item code or product name…"
                                className="w-full rounded-lg border border-neutral-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                                data-testid={`asn-line-search-${idx}`}
                                autoComplete="off"
                              />
                              {suggestions.length > 0 && (
                                <div className="absolute z-10 left-0 right-0 mt-1 rounded-lg border border-neutral-200 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid={`asn-line-suggestions-${idx}`}>
                                  {suggestions.map((p) => (
                                    <button
                                      key={p.id}
                                      type="button"
                                      onClick={() => pickProductForLine(idx, p)}
                                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#DEECF9] flex items-center justify-between gap-2"
                                      data-testid={`asn-line-suggestion-${idx}-${p.sku || p.id}`}
                                    >
                                      <span className="font-mono font-bold text-[#0078D4] shrink-0">{p.sku || '—'}</span>
                                      <span className="text-[#0F172A] truncate flex-1">{p.name}</span>
                                      <span className="text-[10px] text-neutral-400 shrink-0">{p.uom || 'ctn'}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                            <label className="block">
                              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Qty</span>
                              <input
                                type="number"
                                min="1"
                                value={ln.qty}
                                onChange={(e) => patchLine(idx, { qty: e.target.value })}
                                className="mt-0.5 w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                                data-testid={`asn-line-qty-${idx}`}
                              />
                            </label>
                            <label className="block">
                              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">UOM</span>
                              <input
                                type="text"
                                value={ln.uom}
                                onChange={(e) => patchLine(idx, { uom: e.target.value })}
                                className="mt-0.5 w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                                data-testid={`asn-line-uom-${idx}`}
                              />
                            </label>
                            <label className="block">
                              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Prod. date</span>
                              <input
                                type="date"
                                value={ln.production_date}
                                onChange={(e) => patchLine(idx, { production_date: e.target.value })}
                                className="mt-0.5 w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                                data-testid={`asn-line-prod-${idx}`}
                              />
                            </label>
                            <label className="block">
                              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Exp. date</span>
                              <input
                                type="date"
                                value={ln.expiry_date}
                                onChange={(e) => patchLine(idx, { expiry_date: e.target.value })}
                                className="mt-0.5 w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]"
                                data-testid={`asn-line-exp-${idx}`}
                              />
                            </label>
                          </div>
                          {form.line_items.length > 1 && (
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeLine(idx)}
                                className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-800 font-semibold"
                                data-testid={`asn-line-remove-${idx}`}
                              >
                                <Trash2 className="w-3 h-3" /> Remove line
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Feb 2026 — moved from the section header's top-right
                      to a full-width row directly below the last line's
                      QTY / UOM / PROD / EXP inputs so the "next step"
                      after filling in a line is unmistakable on both
                      desktop and handheld. Left-aligned per user
                      spec. */}
                  <button
                    type="button"
                    onClick={addLine}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#0078D4]/40 bg-[#0078D4]/5 hover:bg-[#0078D4]/10 text-[#0078D4] hover:text-[#005A9E] text-sm font-semibold px-3 py-2.5 transition"
                    data-testid="asn-add-line-btn"
                  >
                    <Plus className="w-4 h-4" /> Add line
                  </button>
                </div>
              </div>

              <div className="p-4 border-t border-neutral-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setModalOpen(false); setEditingAsnId(null); }}
                  className="rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-sm font-semibold px-4 py-2"
                  data-testid="asn-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={saving}
                  className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:bg-[#0078D4]/40 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 inline-flex items-center gap-2"
                  data-testid="asn-modal-submit"
                >
                  {saving ? 'Saving…' : (editingAsnId ? 'Save changes' : 'Create ASN')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {splitAsnId && (
        <MultiSplitAssignModal
          open={!!splitAsnId}
          onClose={() => setSplitAsnId(null)}
          scope="asn"
          asnId={splitAsnId}
          users={assignableUsers}
          API={API}
          authHeaders={authHeaders}
          onAssigned={async () => {
            await loadAsnTasks(splitAsnId);
            await refreshAsn(splitAsnId);
          }}
        />
      )}
      {/* Feb 2026 — Post-receive Expected Qty edit modal (audited). */}
      {postReceiveEdit && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={() => { if (!postReceiveSaving) setPostReceiveEdit(null); }}
          data-testid="asn-post-receive-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-amber-700">Audited edit — post-receive</div>
                <div className="text-sm font-semibold text-[#0F172A]">Edit Expected Qty · ASN {postReceiveEdit.asn.asn_number}</div>
              </div>
              <button
                type="button"
                onClick={() => { if (!postReceiveSaving) setPostReceiveEdit(null); }}
                className="text-neutral-400 hover:text-neutral-700"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2">
                Stock, placements and ledger are <strong>never</strong> changed by this edit. Only the ASN's expected amount + variance update. A full audit trail of this change is kept on the ASN.
              </div>
              <div className="text-xs text-neutral-500">
                Line <span className="tabular-nums font-semibold text-neutral-800">#{postReceiveEdit.line.line_no ?? (postReceiveEdit.lineIndex + 1)}</span> · <span className="font-mono text-[#0078D4]">{postReceiveEdit.line.sku || '—'}</span> · <span className="text-neutral-700">{postReceiveEdit.line.name || ''}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1">From</label>
                  <div className="text-sm font-semibold tabular-nums text-neutral-700 bg-neutral-100 rounded-md px-3 py-2">{postReceiveEdit.line.qty}</div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1" htmlFor="post-receive-qty-input">To</label>
                  <input
                    id="post-receive-qty-input"
                    type="number"
                    min="1"
                    step="any"
                    value={postReceiveQty}
                    onChange={(e) => setPostReceiveQty(e.target.value)}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                    data-testid="asn-post-receive-qty-input"
                    autoFocus
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1" htmlFor="post-receive-reason-input">Reason (required)</label>
                <textarea
                  id="post-receive-reason-input"
                  rows={3}
                  value={postReceiveReason}
                  onChange={(e) => setPostReceiveReason(e.target.value)}
                  placeholder="Why is this expected qty being changed after receipt?"
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="asn-post-receive-reason-input"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPostReceiveEdit(null)}
                disabled={postReceiveSaving}
                className="rounded-full border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-100 text-xs font-semibold px-3 py-1.5 transition"
                data-testid="asn-post-receive-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={postReceiveSaving}
                onClick={async () => {
                  const next = Number(postReceiveQty);
                  const reason = (postReceiveReason || '').trim();
                  if (!Number.isFinite(next) || next <= 0) {
                    toast({ title: 'Qty must be greater than zero', variant: 'destructive' });
                    return;
                  }
                  if (!reason) {
                    toast({ title: 'Reason is required', variant: 'destructive' });
                    return;
                  }
                  setPostReceiveSaving(true);
                  try {
                    await axios.patch(
                      `${API}/admin/asn/${postReceiveEdit.asn.id}/line/${postReceiveEdit.lineIndex}/qty-post-receive`,
                      { qty: next, reason },
                      { headers: authHeaders },
                    );
                    toast({
                      title: 'Expected qty updated (audited)',
                      description: `${postReceiveEdit.line.sku || postReceiveEdit.line.name}: ${postReceiveEdit.line.qty} → ${next}`,
                    });
                    setPostReceiveEdit(null);
                    setPostReceiveQty('');
                    setPostReceiveReason('');
                    await load();
                  } catch (e) {
                    toast({ title: 'Could not save', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
                  } finally {
                    setPostReceiveSaving(false);
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-500 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 transition disabled:opacity-50"
                data-testid="asn-post-receive-save"
              >
                <Check className="w-3.5 h-3.5" /> {postReceiveSaving ? 'Saving…' : 'Save edit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminGoodsReceiving;
